import useZoomCharts, { ZoomIndexType } from 'src/Libs/system-charts/shared/hooks/use-zoom-charts'
import { CostSavingChartData } from '../../types'
import { useCallback, useMemo } from 'react'
import { HoverPayload } from 'src/Libs/chart-kit/shared/types'
import { ChartEventHandlers } from 'src/Libs/chart-kit/shared/chart-event-handlers'

interface ReturnType {
  zoomedData: CostSavingChartData[]
  chartEventHandlers: ChartEventHandlers
  resetZoom: () => void
  zoomStartIndex: ZoomIndexType
  zoomEndIndex: ZoomIndexType
  isZoomedIn: boolean
  zoomedFrom: string | number | null
  zoomedTo: string | number | null
}

/**
 * Custom hook to handle chart events and zoom functionality for the cost savings chart.
 * It uses the `useZoomCharts` hook to manage zooming and provides event handlers for mouse interactions.
 *
 * @param data - The data to be displayed in the chart.
 * @returns An object containing the zoomed data, event handlers, and zoom indices.
 */
const useChartEventsAndZoom = (data: CostSavingChartData[]): ReturnType => {
  const {
    zoomedData,
    handleMouseDown: handleZoomMouseDown,
    handleMouseMove: handleZoomMouseMove,
    handleMouseUp: handleZoomMouseUp,
    resetZoom,
    zoomStartIndex,
    zoomEndIndex,
    isZoomedIn,
    zoomedFrom,
    zoomedTo,
  } = useZoomCharts({ data })

  const onMouseDown = useCallback(
    (payload: HoverPayload): void => {
      handleZoomMouseDown?.(payload)
    },
    [handleZoomMouseDown]
  )

  const onMouseMove = useCallback(
    (payload: HoverPayload): void => {
      handleZoomMouseMove?.(payload)
    },
    [handleZoomMouseMove]
  )

  const onMouseUp = useCallback(
    (payload: HoverPayload): void => {
      handleZoomMouseUp?.()
    },
    [handleZoomMouseUp]
  )

  const chartEventHandlers: ChartEventHandlers = useMemo(
    () => ({
      onMouseUp,
      onMouseDown,
      onMouseMove,
    }),
    [onMouseDown, onMouseMove, onMouseUp]
  )

  return { zoomedData, chartEventHandlers, resetZoom, zoomStartIndex, zoomEndIndex, isZoomedIn, zoomedFrom, zoomedTo }
}

export default useChartEventsAndZoom
